/**
 * Cloud layer, drawn from the stable-fluids density texture.
 *
 * Modes (QualityTier.cloudMode):
 * - 'billboards' (low tier): a lattice of soft camera-facing sprites at
 *   mid-cloud height, each sampling the density at its lattice point.
 *   Drawn in the main scene. No offscreen pass, no raymarching.
 * - 'raymarch-quarter' / 'raymarch-half': each pixel marches its view ray
 *   through the cloud slab, sampling density x a vertical profile and
 *   accumulating opacity (Beer-Lambert). Rendered into an offscreen target
 *   with 1/4 or 1/2 of the screen's pixels (linear scale 0.5 or 0.707),
 *   then blended over the frame with premultiplied alpha.
 *
 * Known limitation: the raymarched clouds are composited over the whole
 * frame with no depth test, so debris inside the cloud slab is always
 * drawn behind the clouds. Billboards are depth-tested normally.
 *
 * Coordinates: the group (billboards) and slab bounds follow the storm
 * center, matching the fluid grid, which is storm-relative.
 */
import {
  BackSide,
  BoxGeometry,
  CustomBlending,
  Group,
  InstancedMesh,
  Mesh,
  MeshBasicNodeMaterial,
  NoBlending,
  OneFactor,
  OneMinusSrcAlphaFactor,
  PlaneGeometry,
  QuadMesh,
  RenderTarget,
  Scene,
  Vector2,
  Vector3,
  type Camera,
  type DataTexture,
  type WebGPURenderer,
} from 'three/webgpu';
import {
  Fn,
  Loop,
  cameraPosition,
  exp,
  float,
  instanceIndex,
  length,
  max,
  min,
  mix,
  normalize,
  positionGeometry,
  positionWorld,
  smoothstep,
  texture,
  uniform,
  uv,
  varying,
  vec2,
  vec3,
  vec4,
} from 'three/tsl';
import type { QualityTier } from '../config/tiers';

/** Billboard lattice is BILLBOARD_GRID x BILLBOARD_GRID sprites. */
const BILLBOARD_GRID = 28;
/** Extinction per unit density per km of display path. Visual choice. */
const EXTINCTION_PER_KM = 0.04;

export interface CloudGeometry {
  /** Fluid grid width, m. */
  sizeM: number;
  /** Cloud slab base and top, m (physical). */
  baseM: number;
  topM: number;
}

export interface CloudsHandle {
  /** Billboard sprites live here; add to the main scene. */
  readonly group: Group;
  setTier(tier: QualityTier): void;
  setDensity(tex: DataTexture): void;
  /** Re-place everything after Rmax or vertical stretch changes. Not hot path. */
  setGeometry(geom: CloudGeometry, yScale: number): void;
  /** Storm center in km. Allocation-free. */
  setCenter(xKm: number, zKm: number): void;
  /** Billboard orientation from the camera. Call each frame before rendering. Allocation-free. */
  updateCamera(camera: Camera): void;
  /** After the main scene renders: draw raymarched clouds (no-op in billboard mode). */
  renderOverlay(renderer: WebGPURenderer, camera: Camera): void;
  setVisible(v: boolean): void;
  dispose(): void;
}

export function createClouds(
  initialTier: QualityTier,
  densityTex: DataTexture,
  geom: CloudGeometry,
  yScale: number,
  unitsPerM: number,
): CloudsHandle {
  const group = new Group();
  const densityNode = texture(densityTex);
  let tier = initialTier;
  let visible = true;
  let g = geom;
  let ys = yScale;

  // Slab bounds in world km, updated by setCenter / setGeometry.
  const uBoxMin = uniform(new Vector3());
  const uBoxMax = uniform(new Vector3());
  let halfKm = 0;
  let baseKm = 0;
  let topKm = 0;
  let cx = 0;
  let cz = 0;

  function updateBounds(): void {
    uBoxMin.value.set(cx - halfKm, baseKm, cz - halfKm);
    uBoxMax.value.set(cx + halfKm, topKm, cz + halfKm);
  }

  // ----- Billboards -----
  const spriteGeo = new PlaneGeometry(1, 1);
  const spriteMat = new MeshBasicNodeMaterial({ transparent: true, depthWrite: false });
  // Lattice placement and sprite size, in group-local km (set by setGeometry).
  const uHalf = uniform(0);
  const uCell = uniform(1);
  const uYMid = uniform(0);
  const uSpriteSize = uniform(1);
  const uCamRight = uniform(new Vector3(1, 0, 0));
  const uCamUp = uniform(new Vector3(0, 1, 0));
  {
    // Hand-built billboard. three's billboarding() reads center and scale
    // from the mesh's model matrix, not per-instance matrices, which
    // squashed instanced sprites into streaks. Here the center comes from
    // the lattice index, and the quad corners are offset along the camera's
    // right and up axes (columns 0 and 1 of camera.matrixWorld, set each
    // frame by updateCamera). The group has translation only, so world-space
    // axes equal object-space axes.
    const G = BILLBOARD_GRID;
    const gi = instanceIndex.mod(G).toFloat();
    const gj = instanceIndex.div(G).toFloat();
    const center = vec3(
      uHalf.negate().add(gi.add(0.5).mul(uCell)),
      uYMid,
      uHalf.negate().add(gj.add(0.5).mul(uCell)),
    );
    const right = uCamRight;
    const up = uCamUp;
    spriteMat.positionNode = center
      .add(right.mul(positionGeometry.x.mul(uSpriteSize)))
      .add(up.mul(positionGeometry.y.mul(uSpriteSize)));
    const cellUv = varying(vec2(gi.add(0.5).div(G), gj.add(0.5).div(G)));
    const d = densityNode.sample(cellUv).level(float(0)).r;
    const disk = smoothstep(0.5, 0.15, length(uv().sub(0.5)));
    spriteMat.colorNode = vec3(0.92, 0.93, 0.95);
    spriteMat.opacityNode = d.mul(disk).mul(0.85);
  }
  const sprites = new InstancedMesh(spriteGeo, spriteMat, BILLBOARD_GRID * BILLBOARD_GRID);
  sprites.frustumCulled = false;
  group.add(sprites);

  function placeSprites(): void {
    const cell = (2 * halfKm) / BILLBOARD_GRID;
    uHalf.value = halfKm;
    uCell.value = cell;
    uYMid.value = 0.5 * (baseKm + topKm);
    // Slightly larger than a lattice cell so neighbors overlap.
    uSpriteSize.value = cell * 1.8;
  }

  // ----- Raymarch -----
  const cloudScene = new Scene();
  const slabGeo = new BoxGeometry(1, 1, 1);
  let slabMat = new MeshBasicNodeMaterial();
  const slab = new Mesh(slabGeo, slabMat);
  slab.frustumCulled = false;
  cloudScene.add(slab);

  function buildSlabMaterial(steps: number): MeshBasicNodeMaterial {
    const mat = new MeshBasicNodeMaterial({ side: BackSide, transparent: true, depthWrite: false });
    mat.depthTest = false;
    mat.blending = NoBlending; // the offscreen target is cleared to transparent
    mat.outputNode = Fn(() => {
      const ro = cameraPosition;
      const rd = normalize(positionWorld.sub(cameraPosition));
      // Avoid division by exactly zero in the slab test.
      const rdSafe = vec3(rd.x.add(1e-6), rd.y.add(1e-6), rd.z.add(1e-6));
      const t1 = uBoxMin.sub(ro).div(rdSafe);
      const t2 = uBoxMax.sub(ro).div(rdSafe);
      const tNear = max(max(min(t1.x, t2.x), min(t1.y, t2.y)), min(t1.z, t2.z));
      const tFar = min(min(max(t1.x, t2.x), max(t1.y, t2.y)), max(t1.z, t2.z));
      const t0 = max(tNear, float(0));
      const span = max(tFar.sub(t0), float(0));
      const dt = span.div(steps);
      const trans = float(1).toVar();
      const col = vec3(0, 0, 0).toVar();
      const size = uBoxMax.sub(uBoxMin);
      Loop(steps, ({ i }) => {
        const t = t0.add(dt.mul(float(i).add(0.5)));
        const p = ro.add(rd.mul(t));
        const q = p.sub(uBoxMin).div(size);
        // Vertical profile: 0 at base and top, 1 mid-slab.
        const prof = q.y.mul(float(1).sub(q.y)).mul(4);
        const dens = densityNode
          .sample(vec2(q.x, q.z))
          .level(float(0))
          .r.mul(max(prof, float(0)));
        const a = float(1).sub(exp(dens.mul(-EXTINCTION_PER_KM).mul(dt)));
        // Lighter toward the top, darker underneath.
        const shade = mix(vec3(0.55, 0.58, 0.63), vec3(0.97, 0.97, 0.98), q.y);
        col.addAssign(shade.mul(a).mul(trans));
        trans.mulAssign(float(1).sub(a));
      });
      return vec4(col, float(1).sub(trans));
    })();
    return mat;
  }

  let target: RenderTarget | null = null;
  const compositeMat = new MeshBasicNodeMaterial({
    transparent: true,
    depthTest: false,
    depthWrite: false,
  });
  compositeMat.blending = CustomBlending;
  compositeMat.blendSrc = OneFactor;
  compositeMat.blendDst = OneMinusSrcAlphaFactor;
  compositeMat.blendSrcAlpha = OneFactor;
  compositeMat.blendDstAlpha = OneMinusSrcAlphaFactor;
  const compositeTex = texture(densityTex); // replaced with the target texture below
  compositeMat.outputNode = compositeTex.sample(uv()).level(float(0));
  const quad = new QuadMesh(compositeMat);

  function raymarchScale(): number {
    return tier.cloudMode === 'raymarch-quarter' ? 0.5 : Math.SQRT1_2;
  }

  function applyTier(): void {
    sprites.visible = visible && tier.cloudMode === 'billboards';
    if (tier.cloudMode !== 'billboards') {
      slabMat.dispose();
      slabMat = buildSlabMaterial(Math.max(1, tier.cloudSteps));
      slab.material = slabMat;
    }
  }

  function setGeometry(next: CloudGeometry, yScale: number): void {
    g = next;
    ys = yScale;
    halfKm = (g.sizeM / 2) * unitsPerM;
    baseKm = g.baseM * unitsPerM * ys;
    topKm = g.topM * unitsPerM * ys;
    slab.scale.set(2 * halfKm, topKm - baseKm, 2 * halfKm);
    slab.position.set(cx, 0.5 * (baseKm + topKm), cz);
    placeSprites();
    updateBounds();
  }

  setGeometry(g, ys);
  applyTier();

  const tmpSize = new Vector2();

  return {
    group,
    setTier(next: QualityTier): void {
      const modeChanged = next.cloudMode !== tier.cloudMode || next.cloudSteps !== tier.cloudSteps;
      tier = next;
      if (modeChanged) applyTier();
    },
    setDensity(tex: DataTexture): void {
      densityNode.value = tex;
    },
    setGeometry,
    setCenter(xKm: number, zKm: number): void {
      cx = xKm;
      cz = zKm;
      group.position.set(xKm, 0, zKm);
      slab.position.x = xKm;
      slab.position.z = zKm;
      updateBounds();
    },
    updateCamera(camera: Camera): void {
      const e = camera.matrixWorld.elements;
      uCamRight.value.set(e[0], e[1], e[2]);
      uCamUp.value.set(e[4], e[5], e[6]);
    },
    renderOverlay(renderer: WebGPURenderer, camera: Camera): void {
      if (!visible || tier.cloudMode === 'billboards') return;
      renderer.getDrawingBufferSize(tmpSize);
      const s = raymarchScale();
      const w = Math.max(1, Math.round(tmpSize.x * s));
      const h = Math.max(1, Math.round(tmpSize.y * s));
      if (!target) {
        target = new RenderTarget(w, h);
        compositeTex.value = target.texture;
      } else if (target.width !== w || target.height !== h) {
        target.setSize(w, h);
      }
      const prevTarget = renderer.getRenderTarget();
      const prevAutoClear = renderer.autoClear;
      renderer.setRenderTarget(target);
      renderer.setClearColor(0x000000, 0);
      renderer.clear();
      renderer.render(cloudScene, camera);
      renderer.setRenderTarget(prevTarget);
      renderer.autoClear = false;
      quad.render(renderer);
      renderer.autoClear = prevAutoClear;
    },
    setVisible(v: boolean): void {
      visible = v;
      sprites.visible = v && tier.cloudMode === 'billboards';
    },
    dispose(): void {
      spriteGeo.dispose();
      spriteMat.dispose();
      sprites.dispose();
      slabGeo.dispose();
      slabMat.dispose();
      compositeMat.dispose();
      target?.dispose();
    },
  };
}
